import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Form, notification } from 'antd'
import { setLoading } from '../../../redux/store/common/slice'
import {
  getCCReassignmentOptions,
  getCCRelatedItemsRequest,
  getReassignmentRequestDataRequest,
  getUserContractCategories,
  reassignCCResponsibleRequest,
  unassignCCRequest,
  updateReassignmentRequestDataRequest,
  getCCAssignmentOptions
} from '../../../features/UserContractCategories/api'
import { DEFAULT_PAGE } from '../../../components/Table/constants'
import {
  CCReassignmentRequest,
  CCReassignRequestDataFormat,
  CCReassignmentOptionsResponse,
  TCCRelatedItemReassign,
  TCCRelatedItems,
  UserContractCategoriesParams,
  UserContractCategoriesRequestData,
  UserContractCategory
} from '../../../features/UserContractCategories/types'
import { ResponseWithPagination } from 'constants/types'
import { TPageInfo } from '../../../components/Table/types'
import { TUserForm } from 'components/Forms/forms.user.d'
import { getUser } from '../../../redux/store/user/getters'
import { ACTIONS, ROLES, SUBJECTS } from '../../../features/Permission'
import { useCRUDUserPopupProviderContext } from './CRUDUserPopupProvider'
import { formatRelatedItems, prepareRelatedItems } from './utils'
import { TOption } from '../../../components/Select/types'
import { TCCRelatedItemReassignWithCheckbox } from '../../../features/UserContractCategories/ManageAssignmentPopupWithoutItems/types'
import { FormInstance } from 'antd/lib/form/hooks/useForm'
import { initialCCRelatedItems } from './constants'
import { useDebounce } from '../../../hooks/hooks'
import defineAbilityFor from '../../../features/Permission/ability'

type ContextProps = {
  state: {
    firstLoad: boolean
    isReassign: boolean
    totalNumber: number
    searchValue: string
    debouncedSearch: string
    sortParams?: UserContractCategoriesRequestData
    pageInfo: TPageInfo
    isOwnProfile: boolean
    tableData: UserContractCategory[]
    userData: Partial<TUserForm>
    CCRelatedItems: TCCRelatedItems
    selectedCC: UserContractCategory | null
    ccOwnerOptions: TOption[]
    profileUserRole: string
    canSeeExtendedViewMode: boolean
    canViewUserContractCategories: boolean
    canManageContractCategories: boolean
    canEditCommunityLevelCheckboxes: boolean
    canViewCommunityLevelCheckboxes: boolean
    canManageCategoryReassignment: boolean
    canViewCommunityContractCategories: boolean
    canCreateCommunityContractCategories: boolean
    canUpdateCommunityContractCategories: boolean
    canViewUserContractCategoriesSettings: boolean
    canViewFormContractCategoriesInput: boolean
    canViewFormHospitalList: boolean
    canApprovePendingUsers: boolean
    canViewVotingPanelMembership: boolean
    canUpdateVotingPanelMembership: boolean
    reassignmentRequest: CCReassignmentRequest | null
    userForm: FormInstance<TUserForm>
    form: FormInstance<
      TCCRelatedItemReassignWithCheckbox | TCCRelatedItemReassign
    >
  }
  actions: {
    setPageInfo: Dispatch<SetStateAction<TPageInfo>>
    setUserData: Dispatch<SetStateAction<Partial<TUserForm>>>
    setSearchValue: Dispatch<SetStateAction<Partial<string>>>
    getContractCategories: (
      args: UserContractCategoriesRequestData
    ) => Promise<void>
    setSelectedCC: Dispatch<SetStateAction<UserContractCategory | null>>
    reassignCCResponsible: (
      id: string,
      data: CCReassignRequestDataFormat
    ) => Promise<void>
    updateReassignmentRequest: (
      id: string,
      data: CCReassignRequestDataFormat
    ) => Promise<void>
    unassignRequest: (
      id: string,
      data: CCReassignRequestDataFormat
    ) => Promise<void>
  }
}

const CRUDUserContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const CRUDUserContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [searchValue, setSearchValue] = useState('')
  const [firstLoad, setFirstLoad] = useState(true)
  const [totalNumber, setTotalNumber] = useState(0)
  const [userData, setUserData] = useState<Partial<TUserForm>>({})
  const [sortParams, setSortParams] =
    useState<UserContractCategoriesRequestData>()
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [tableData, setTableData] = useState<UserContractCategory[]>([])
  const [CCRelatedItems, setCCRelatedItems] = useState<TCCRelatedItems>(
    initialCCRelatedItems
  )
  const [selectedCC, setSelectedCC] = useState<UserContractCategory | null>(
    null
  )

  const [ccOwnerOptions, setCcOwnerOptions] = useState<TOption[]>([])

  const [reassignmentRequest, setReassignmentRequest] =
    useState<CCReassignmentRequest | null>(null)

  const { assignWithRelatedItemsHSPresPopup, assignWithoutRelatedItemsPopup } =
    useCRUDUserPopupProviderContext()

  const user = useSelector(getUser)
  const debouncedSearch = useDebounce(searchValue, 500)
  const dispatch = useDispatch()

  const [form] = Form.useForm<
    TCCRelatedItemReassignWithCheckbox | TCCRelatedItemReassign
  >()
  const [userForm] = Form.useForm<TUserForm>()

  const watchIsReassign = Form.useWatch('isAssignAnotherChecked', form)
  const watchRole: string = Form.useWatch('role', userForm)
  const profileUserRole: string = watchRole || (userData.role as string)
  const ability = defineAbilityFor(profileUserRole, user, userData.uuid ?? '')

  const isOwnProfile = useMemo(
    () => user.uuid === userData.uuid,
    [user.uuid, userData.uuid]
  )

  const canSeeExtendedViewMode = useMemo(
    () =>
      ability.can(
        ACTIONS.VIEW,
        SUBJECTS.MY_COMMUNITY_USER_CONTRACT_CATEGORIES_EXTENDED_VIEW_MODE
      ),
    [ability]
  )

  const canViewUserContractCategories = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.USER_CONTRACT_CATEGORIES),
    [ability]
  )

  const canManageContractCategories = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.USER_CONTRACT_CATEGORIES_ACTIONS),
    [ability]
  )

  const canEditCommunityLevelCheckboxes = useMemo(
    () =>
      ability.can(
        ACTIONS.CRUD,
        SUBJECTS.USER_CONTRACT_CATEGORIES_COMMUNITY_LEVEL
      ),
    [ability]
  )

  const canViewCommunityLevelCheckboxes = useMemo(
    () =>
      ability.can(
        ACTIONS.VIEW,
        SUBJECTS.USER_CONTRACT_CATEGORIES_COMMUNITY_LEVEL
      ),
    [ability]
  )

  const canManageCategoryReassignment = useMemo(
    () =>
      ability.can(
        ACTIONS.UPDATE,
        SUBJECTS.MY_COMMUNITY_USER_CONTRACT_CATEGORIES
      ),
    [ability]
  )

  const canViewCommunityContractCategories = useMemo(
    () =>
      ability.can(ACTIONS.VIEW, SUBJECTS.MY_COMMUNITY_USER_CONTRACT_CATEGORIES),
    [ability]
  )

  const canCreateCommunityContractCategories = useMemo(
    () =>
      ability.can(
        ACTIONS.CREATE,
        SUBJECTS.MY_COMMUNITY_USER_CONTRACT_CATEGORIES
      ),
    [ability]
  )

  const canUpdateCommunityContractCategories = useMemo(
    () =>
      ability.can(
        ACTIONS.UPDATE,
        SUBJECTS.MY_COMMUNITY_USER_CONTRACT_CATEGORIES
      ),
    [ability]
  )

  const canViewUserContractCategoriesSettings = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.USER_CONTRACT_CATEGORIES_SETTINGS),
    [ability]
  )

  const canApprovePendingUsers = useMemo(
    () => ability.can(ACTIONS.CRUD, SUBJECTS.APPROVE_PENDING_USERS),
    [ability]
  )

  const canViewFormHospitalList = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.FORM_HOSPITAL_LIST),
    [ability]
  )

  const canViewFormContractCategoriesInput = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.FORM_CONTARCT_CATEGORIES_INPUT),
    [ability]
  )

  const canViewVotingPanelMembership = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.VOTING_PANEL_MEMBERSHIP),
    [ability]
  )

  const canUpdateVotingPanelMembership = useMemo(
    () => ability.can(ACTIONS.UPDATE, SUBJECTS.VOTING_PANEL_MEMBERSHIP),
    [ability]
  )

  const isReassign = useMemo(
    () =>
      (user.role === ROLES.COMMUNITY_PRESIDENT &&
        !!reassignmentRequest?.uuid) ||
      watchIsReassign ||
      !!CCRelatedItems.contracts.length ||
      !!CCRelatedItems.rfp.length,
    [
      user.role,
      reassignmentRequest?.uuid,
      watchIsReassign,
      CCRelatedItems.contracts.length,
      CCRelatedItems.rfp.length
    ]
  )

  useEffect(() => {
    if (userData.uuid) {
      setPageInfo({ ...DEFAULT_PAGE })
      void getContractCategories({
        ...sortParams,
        page: { ...DEFAULT_PAGE },
        search: debouncedSearch
      })
    }
    // eslint-disable-next-line
  }, [userData.uuid, debouncedSearch])

  useEffect(() => {
    if (selectedCC?.uuid) {
      if (
        user.role === ROLES.VICE_PRESIDENT ||
        user.role === ROLES.COMMUNITY_PRESIDENT ||
        user.role === ROLES.COMMONS_VICE_PRESIDENT
      ) {
        void getCCRelatedItems(selectedCC)
      } else {
        assignWithoutRelatedItemsPopup.actions.open()
        void loadReassignmentOptions(
          selectedCC.uuid,
          selectedCC.reassignment_request?.uuid
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCC, user.role])

  const getContractCategories = useCallback(
    async ({
      sortField,
      sortOrder,
      page,
      search
    }: UserContractCategoriesRequestData) => {
      try {
        dispatch(setLoading(true))
        const params: UserContractCategoriesParams = {}
        if (sortField) {
          if (sortOrder) {
            params.ordering = `${
              sortOrder === 'descend' ? '-' : ''
            }${sortField}`
          }
          setSortParams({
            sortField,
            sortOrder
          })
        }
        if (search) {
          params.search = search
        }
        const dataPage = page ? page : pageInfo
        params.limit = dataPage.pageSize
        params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
        const { data } = await getUserContractCategories(
          userData.uuid as string,
          params
        )
        setTableData(
          data.results.map((i) => ({
            ...i,
            key: i.uuid
          }))
        )
        if (!search) {
          setTotalNumber(data.count)
        }
        if (firstLoad) {
          setFirstLoad(false)
        }
      } finally {
        dispatch(setLoading(false))
      }
    },
    [dispatch, firstLoad, pageInfo, userData.uuid]
  )

  const loadReassignmentOptions = useCallback(
    async (ccId: string, reassignmentId?: string) => {
      let responseData: ResponseWithPagination<CCReassignmentOptionsResponse> =
        { count: 0, results: [] }
      if (reassignmentId) {
        const { data } = await getCCReassignmentOptions(
          userData.uuid,
          ccId,
          reassignmentId
        )
        responseData = data
      } else {
        const { data } = await getCCAssignmentOptions({
          userId: userData.uuid,
          ccId
        })
        responseData = data
      }
      setCcOwnerOptions(
        responseData.results.map((user) => ({
          value: user.uuid,
          label: `${user.first_name} ${user.last_name}`
        }))
      )
    },
    [userData.uuid]
  )

  const checkRelatedItems = useCallback(
    (relatedItems: TCCRelatedItems) => {
      if (!!relatedItems.rfp.length || !!relatedItems.contracts.length) {
        assignWithRelatedItemsHSPresPopup.actions.open()
        setCCRelatedItems(relatedItems)
      } else {
        assignWithoutRelatedItemsPopup.actions.open()
        setCCRelatedItems(initialCCRelatedItems)
      }
    },
    [
      assignWithoutRelatedItemsPopup.actions,
      assignWithRelatedItemsHSPresPopup.actions
    ]
  )

  const getCCRelatedItems = useCallback(
    async ({ uuid, reassignment_request }: UserContractCategory) => {
      if (!userData.uuid) return
      try {
        dispatch(setLoading(true))
        if (reassignment_request?.uuid) {
          const { data } = await getReassignmentRequestDataRequest({
            userId: userData.uuid,
            ccId: uuid,
            reassignmentId: reassignment_request.uuid
          })
          const { rfpAssignee, rfp, contractAssignee, contracts } =
            prepareRelatedItems(data)
          if (data.assignee_set) {
            data.assignee_set.rfp_assignee_set = rfpAssignee
            data.assignee_set.org_cc_assignee_set = contractAssignee
          }
          setReassignmentRequest(data)
          checkRelatedItems({ rfp, contracts })
        } else {
          setReassignmentRequest(null)
          const { data } = await getCCRelatedItemsRequest(userData.uuid, uuid)
          checkRelatedItems(formatRelatedItems(data))
        }
        await loadReassignmentOptions(uuid, reassignment_request?.uuid)
      } catch (err) {
        console.error(err)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [userData.uuid, dispatch, loadReassignmentOptions, checkRelatedItems]
  )

  const successfulReassignmentCallback = useCallback(async () => {
    assignWithoutRelatedItemsPopup.actions.close()
    assignWithRelatedItemsHSPresPopup.actions.close()
    await getContractCategories({
      ...sortParams,
      page: pageInfo,
      search: debouncedSearch
    })
    let message =
      user.role === ROLES.COMMUNITY_PRESIDENT
        ? 'Contract category has been successfully removed.'
        : 'Contract category has been marked for removal.'
    if (user.role === ROLES.COMMUNITY_PRESIDENT && reassignmentRequest?.uuid) {
      message = 'You successfully approved the removal'
    }
    if (isReassign) {
      message =
        user.role === ROLES.COMMUNITY_PRESIDENT
          ? 'Contract category has been successfully reassigned.'
          : 'Contract category has been marked for reassignment.'
      if (
        user.role === ROLES.COMMUNITY_PRESIDENT &&
        reassignmentRequest?.uuid
      ) {
        message = 'You successfully approved the reassignment'
      }
    }
    notification.success({ message })
    form.resetFields()
    setCCRelatedItems(initialCCRelatedItems)
    setSelectedCC(null)
    setReassignmentRequest(null)
  }, [
    assignWithoutRelatedItemsPopup.actions,
    assignWithRelatedItemsHSPresPopup.actions,
    getContractCategories,
    sortParams,
    pageInfo,
    debouncedSearch,
    user.role,
    reassignmentRequest?.uuid,
    isReassign,
    form
  ])

  const reassignCCResponsible = useCallback(
    async (id: string, data: CCReassignRequestDataFormat) => {
      dispatch(setLoading(true))
      await reassignCCResponsibleRequest({
        userId: userData.uuid,
        ccId: id,
        data
      })
        .then(successfulReassignmentCallback)
        .catch((err) => {
          if (err.data.cc_rfp_reassignment)
            notification.error({
              message: err.data.cc_rfp_reassignment[0]
            })
          console.error(err)
        })
        .finally(() => {
          dispatch(setLoading(false))
        })
    },
    [dispatch, userData.uuid, successfulReassignmentCallback]
  )

  const updateReassignmentRequest = useCallback(
    async (id: string, data: CCReassignRequestDataFormat) => {
      if (!reassignmentRequest?.uuid) return
      dispatch(setLoading(true))
      await updateReassignmentRequestDataRequest({
        userId: userData.uuid,
        ccId: id,
        reassignmentId: reassignmentRequest?.uuid,
        data
      })
        .then(successfulReassignmentCallback)
        .catch((err) => {
          if (err.data.cc_rfp_reassignment)
            notification.error({
              message: err.data.cc_rfp_reassignment[0]
            })
          console.error(err)
        })
        .finally(() => {
          dispatch(setLoading(false))
        })
    },
    [
      reassignmentRequest?.uuid,
      dispatch,
      userData.uuid,
      successfulReassignmentCallback
    ]
  )

  const unassignRequest = useCallback(
    async (id: string, data: CCReassignRequestDataFormat) => {
      try {
        dispatch(setLoading(true))
        await unassignCCRequest(userData.uuid, id, data)
        await successfulReassignmentCallback()
      } finally {
        dispatch(setLoading(false))
      }
    },
    [dispatch, userData.uuid, successfulReassignmentCallback]
  )

  const context = useMemo(
    () => ({
      state: {
        firstLoad,
        totalNumber,
        sortParams,
        pageInfo,
        tableData,
        searchValue,
        isOwnProfile,
        debouncedSearch,
        userData,
        CCRelatedItems,
        selectedCC,
        ccOwnerOptions,
        reassignmentRequest,
        canSeeExtendedViewMode,
        canViewUserContractCategories,
        canManageContractCategories,
        canEditCommunityLevelCheckboxes,
        canViewCommunityLevelCheckboxes,
        canManageCategoryReassignment,
        canViewCommunityContractCategories,
        canCreateCommunityContractCategories,
        canUpdateCommunityContractCategories,
        canViewUserContractCategoriesSettings,
        canViewFormContractCategoriesInput,
        canApprovePendingUsers,
        canViewFormHospitalList,
        canViewVotingPanelMembership,
        canUpdateVotingPanelMembership,
        profileUserRole,
        isReassign,
        userForm,
        form
      },
      actions: {
        getContractCategories,
        setPageInfo,
        setUserData,
        setSelectedCC,
        setSearchValue,
        reassignCCResponsible,
        updateReassignmentRequest,
        unassignRequest
      }
    }),
    [
      firstLoad,
      totalNumber,
      sortParams,
      pageInfo,
      searchValue,
      isOwnProfile,
      debouncedSearch,
      tableData,
      userData,
      CCRelatedItems,
      selectedCC,
      ccOwnerOptions,
      reassignmentRequest,
      canSeeExtendedViewMode,
      canViewUserContractCategories,
      canManageContractCategories,
      canEditCommunityLevelCheckboxes,
      canViewCommunityLevelCheckboxes,
      canManageCategoryReassignment,
      canViewCommunityContractCategories,
      canCreateCommunityContractCategories,
      canUpdateCommunityContractCategories,
      canViewUserContractCategoriesSettings,
      canViewFormContractCategoriesInput,
      canApprovePendingUsers,
      canViewFormHospitalList,
      canViewVotingPanelMembership,
      canUpdateVotingPanelMembership,
      profileUserRole,
      isReassign,
      userForm,
      form,
      getContractCategories,
      reassignCCResponsible,
      updateReassignmentRequest,
      unassignRequest
    ]
  )

  return (
    <CRUDUserContext.Provider value={context}>
      {children}
    </CRUDUserContext.Provider>
  )
}

export const useCRUDUserContext = () => useContext(CRUDUserContext)

export default CRUDUserContextProvider
